import React, { useRef } from 'react';
import { Upload, FileSearch, FileText, Zap, Clock, DollarSign, TrendingUp, BarChart3, Layers, CheckCircle, Wrench, Flame, Hammer } from 'lucide-react';
export function TapeMeasureAI() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };
  const handleContactClick = () => {
    window.location.href = 'mailto:james-mares@tapemeasureai.com?subject=Demo Request&body=Hi, I would like to schedule a demo of Tape Measure AI.';
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      console.log('Files selected:', files);
      // Handle file upload logic here
    }
  };
  return <div className="w-full min-h-screen bg-[#2d2d2d] text-white" data-id="element-5">
      {/* Hero Section */}
      <section className="px-6 md:px-16 py-20 max-w-7xl mx-auto" data-id="element-6">
        <div className="grid md:grid-cols-[300px_1fr] gap-12 items-center" data-id="element-7">
          {/* Logo */}
          <div className="rounded-lg p-8 flex items-center justify-center h-[280px]" data-id="element-8">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nbSehCLSo6DLdthM2Kv745/ChatGPT_Image_Oct_28%2C_2025%2C_08_54_29_PM.png" alt="Tape Measure AI Logo" className="w-full h-full object-contain" data-id="element-9" />
          </div>
          {/* Hero Text */}
          <div data-id="element-10">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-white to-gray-400 bg-clip-text text-transparent leading-tight" data-id="element-11">
              Tape Measure AI: Turn Drawings Into Accurate Quotes In Minutes
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed mb-6" data-id="element-12">
              Stop spending overhead hours manually parsing and estimating
              project plans and shop drawings. Tape Measure AI transforms
              complex project drawings into accurate, trade specific quotes in
              minutes, not days. Simply upload your files and let our
              intelligent platform handle the heavy lifting.
            </p>
            <div className="flex justify-center" data-id="element-13">
              <a href="mailto:james-mares@tapemeasureai.com?subject=Demo Request&body=Hi, I would like to schedule a demo of Tape Measure AI." className="bg-[#d4a855] hover:bg-[#c49945] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors inline-block" data-id="element-14">
                Contact for Demo
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* Transform Section */}
      <section className="px-6 md:px-16 py-20 bg-[#252525]" data-id="element-15">
        <div className="max-w-7xl mx-auto" data-id="element-16">
          <h2 className="text-4xl font-bold mb-12 text-center" data-id="element-17">
            Transform Your Estimating Process With AI-Powered Precision
          </h2>
          <div className="grid md:grid-cols-2 gap-12" data-id="element-18">
            {/* The Old Way */}
            <div data-id="element-19">
              <h3 className="text-2xl font-semibold mb-6" data-id="element-20">The Old Way</h3>
              <ul className="space-y-4 text-gray-300" data-id="element-21">
                <li className="flex items-start gap-3" data-id="element-22">
                  <span className="text-red-400 mt-1" data-id="element-23">✕</span>
                  <span data-id="element-24">
                    Manual ERP itemization taking hours or days per project
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-25">
                  <span className="text-red-400 mt-1" data-id="element-26">✕</span>
                  <span data-id="element-27">
                    Tedious page by page review with error prone calculations
                    and missed materials on big projects
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-28">
                  <span className="text-red-400 mt-1" data-id="element-29">✕</span>
                  <span data-id="element-30">
                    Excel-based calculations prone to outdated prices{' '}
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-31">
                  <span className="text-red-400 mt-1" data-id="element-32">✕</span>
                  <span data-id="element-33">Inconsistent quote formats across teams</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-34">
                  <span className="text-red-400 mt-1" data-id="element-35">✕</span>
                  <span data-id="element-36">
                    Days and weeks long turnaround times leading to lost bids
                  </span>
                </li>
              </ul>
            </div>
            {/* The Tape Measure AI Way */}
            <div data-id="element-37">
              <h3 className="text-2xl font-semibold mb-6" data-id="element-38">
                The Tape Measure AI Way
              </h3>
              <ul className="space-y-4 text-gray-300" data-id="element-39">
                <li className="flex items-start gap-3" data-id="element-40">
                  <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={20} data-id="element-41" />
                  <span data-id="element-42">Automated upload of all documents in one batch</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-43">
                  <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={20} data-id="element-44" />
                  <span data-id="element-45">
                    AI-powered extraction of measurements, material take-offs,
                    labor and specs
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-46">
                  <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={20} data-id="element-47" />
                  <span data-id="element-48">
                    Intelligent cost calculations using your custom pricing
                    model
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-49">
                  <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={20} data-id="element-50" />
                  <span data-id="element-51">
                    Professional, branded proposals intergrated with your
                    preffered book-keeping system
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-52">
                  <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={20} data-id="element-53" />
                  <span data-id="element-54">Rapid response time that wins more bids</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="px-6 md:px-16 py-20" data-id="element-55">
        <div className="max-w-7xl mx-auto" data-id="element-56">
          <h2 className="text-4xl font-bold mb-4 text-center" data-id="element-57">
            How It Works: Upload, Extract, Quote
          </h2>
          <p className="text-center text-gray-300 mb-12 max-w-3xl mx-auto" data-id="element-58">
            Our platform combines AI vision models with deep construction
            industry knowledge to deliver quotes you can trust. Built
            specifically for contractors and subcontractors, Tape Measure AI
            understands the nuances of different trades and project types.
          </p>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-59">
            {/* Upload */}
            <div className="bg-[#252525] rounded-lg p-8" data-id="element-60">
              <div className="bg-[#3a3a3a] w-16 h-16 rounded-lg flex items-center justify-center mb-6" data-id="element-61">
                <Upload className="text-blue-400" size={32} data-id="element-62" />
              </div>
              <h3 className="text-xl font-semibold mb-4" data-id="element-63">Upload Drawings</h3>
              <p className="text-gray-300" data-id="element-64">
                Drop entire PDF folders or multiple files at once. Our platform
                handles every trades specific needs.
              </p>
            </div>
            {/* Extract */}
            <div className="bg-[#252525] rounded-lg p-8" data-id="element-65">
              <div className="bg-[#3a3a3a] w-16 h-16 rounded-lg flex items-center justify-center mb-6" data-id="element-66">
                <FileSearch className="text-purple-400" size={32} data-id="element-67" />
              </div>
              <h3 className="text-xl font-semibold mb-4" data-id="element-68">AI Extraction</h3>
              <p className="text-gray-300" data-id="element-69">
                Advanced AI reads dimensions, counts components, identifies
                materials, and extracts specifications to your custom format
                with precision.
              </p>
            </div>
            {/* Quote */}
            <div className="bg-[#252525] rounded-lg p-8" data-id="element-70">
              <div className="bg-[#3a3a3a] w-16 h-16 rounded-lg flex items-center justify-center mb-6" data-id="element-71">
                <FileText className="text-green-400" size={32} data-id="element-72" />
              </div>
              <h3 className="text-xl font-semibold mb-4" data-id="element-73">Generate Quote</h3>
              <p className="text-gray-300" data-id="element-74">
                Instantly generate professional estimates with your branding.
                Export in your preferred format or integrate directly with your
                CRM.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Supported File Formats */}
      <section className="px-6 md:px-16 py-20 bg-[#252525]" data-id="element-75">
        <div className="max-w-7xl mx-auto" data-id="element-76">
          <div className="bg-[#2d2d2d] rounded-lg p-8" data-id="element-77">
            <h3 className="text-2xl font-semibold mb-6" data-id="element-78">
              Supported File Formats
            </h3>
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-3" data-id="element-79">
              <ul className="space-y-2 text-gray-300" data-id="element-80">
                <li className="flex items-center gap-2" data-id="element-81">
                  <CheckCircle className="text-green-400 flex-shrink-0" size={18} data-id="element-82" />
                  <span data-id="element-83">PDF</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-84">
                  <CheckCircle className="text-green-400 flex-shrink-0" size={18} data-id="element-85" />
                  <span data-id="element-86">DWG (AutoCAD files)</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-87">
                  <CheckCircle className="text-green-400 flex-shrink-0" size={18} data-id="element-88" />
                  <span data-id="element-89">DXF</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-90">
                  <CheckCircle className="text-green-400 flex-shrink-0" size={18} data-id="element-91" />
                  <span data-id="element-92">Scanned drawing images (JPG, PNG, TIFF)</span>
                </li>
              </ul>
              <ul className="space-y-2 text-gray-300" data-id="element-93">
                <li className="flex items-center gap-2" data-id="element-94">
                  <CheckCircle className="text-green-400 flex-shrink-0" size={18} data-id="element-95" />
                  <span data-id="element-96">Revit exports</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-97">
                  <CheckCircle className="text-green-400 flex-shrink-0" size={18} data-id="element-98" />
                  <span data-id="element-99">IFC (Industry Foundation Classes)</span>
                </li>
                <li className="flex items-center gap-2" data-id="element-100">
                  <CheckCircle className="text-green-400 flex-shrink-0" size={18} data-id="element-101" />
                  <span data-id="element-102">Multi-page PDFs with mixed content</span>
                </li>
              </ul>
            </div>
            <p className="text-gray-400 mt-6" data-id="element-103">
              Our platform handles even poor quality scans, extracting data from
              older blueprints, faxed documents, and photographed drawings. If
              you can read it, we can process it.
            </p>
          </div>
        </div>
      </section>
      {/* Built For The Trades */}
      <section className="px-6 md:px-16 py-20" data-id="element-104">
        <div className="max-w-7xl mx-auto" data-id="element-105">
          <h2 className="text-4xl font-bold mb-12 text-center" data-id="element-106">
            Built For The Trades, By People Who Know The Trades
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-107">
            <div className="rounded-lg overflow-hidden" data-id="element-108">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/ndZJx61hPcka6KPpqoGFAM/20j1K2kbQuvbDYBONu2QN.png" alt="Ironworkers hanging structural steel" className="w-full h-[400px] object-cover" data-id="element-109" />
            </div>
            <div data-id="element-110">
              <p className="text-lg text-gray-300 leading-relaxed" data-id="element-111">
                We know how hard trades like electrical, plumbing, HVAC, piping,
                metal fabrication, carpentry, and others work to win projects.
                That's why we built Tape Measure AI to help you quote faster,
                more accurately, and with confidence. Our platform understands
                trade-specific requirements and delivers estimates that reflect
                the reality of your work.
              </p>
            </div>
          </div>
          {/* Trade Icons */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8" data-id="element-112">
            <div className="text-center" data-id="element-113">
              <div className="bg-[#3a3a3a] w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-114">
                <Zap className="text-yellow-400" size={32} data-id="element-115" />
              </div>
              <h4 className="font-semibold mb-2" data-id="element-116">Electrical</h4>
              <p className="text-sm text-gray-400" data-id="element-117">
                Wire, conduit, fixtures, panels, and circuit design
              </p>
            </div>
            <div className="text-center" data-id="element-118">
              <div className="bg-[#3a3a3a] w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-119">
                <div className="text-blue-400 text-2xl" data-id="element-120">🚿</div>
              </div>
              <h4 className="font-semibold mb-2" data-id="element-121">Plumbing</h4>
              <p className="text-sm text-gray-400" data-id="element-122">
                PVC, copper pipe, fittings, fixtures, water-waste systems
              </p>
            </div>
            <div className="text-center" data-id="element-123">
              <div className="bg-[#3a3a3a] w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-124">
                <div className="text-red-400 text-2xl" data-id="element-125">❄️</div>
              </div>
              <h4 className="font-semibold mb-2" data-id="element-126">HVAC</h4>
              <p className="text-sm text-gray-400" data-id="element-127">
                Ductwork, vents, equipment specs, BTU calculations
              </p>
            </div>
            <div className="text-center" data-id="element-128">
              <div className="bg-[#3a3a3a] w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-129">
                <Wrench className="text-purple-400" size={32} data-id="element-130" />
              </div>
              <h4 className="font-semibold mb-2" data-id="element-131">Piping</h4>
              <p className="text-sm text-gray-400" data-id="element-132">
                Piping SCH, supports, weldolets, flanges
              </p>
            </div>
            <div className="text-center" data-id="element-133">
              <div className="bg-[#3a3a3a] w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-134">
                <Flame className="text-orange-400" size={32} data-id="element-135" />
              </div>
              <h4 className="font-semibold mb-2" data-id="element-136">Metal Fabrication</h4>
              <p className="text-sm text-gray-400" data-id="element-137">
                Structural steel, ornamental, weld symbols, rigging
              </p>
            </div>
            <div className="text-center" data-id="element-138">
              <div className="bg-[#3a3a3a] w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-139">
                <Hammer className="text-amber-400" size={32} data-id="element-140" />
              </div>
              <h4 className="font-semibold mb-2" data-id="element-141">Carpentry</h4>
              <p className="text-sm text-gray-400" data-id="element-142">
                Framing, trim, cabinetry, lumber, finish work
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Custom Cost Models */}
      <section className="px-6 md:px-16 py-20 bg-[#252525]" data-id="element-143">
        <div className="max-w-7xl mx-auto" data-id="element-144">
          <h2 className="text-4xl font-bold mb-4 text-center" data-id="element-145">
            Custom Cost Models Tailored To Your Business
          </h2>
          <p className="text-center text-gray-300 mb-12 max-w-3xl mx-auto" data-id="element-146">
            Every business has unique pricing structures, preferred vendors, and
            regional cost variations. Tape Measure AI adapts to your needs with
            fully customizable cost models.
          </p>
          <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-147">
            <div className="bg-[#2d2d2d] rounded-lg p-8" data-id="element-148">
              <h3 className="text-xl font-semibold mb-4" data-id="element-149">
                Import Your History
              </h3>
              <p className="text-gray-300" data-id="element-150">
                Upload past project estimates and let our AI learn your pricing
                patterns. The system adapts to your historical data, ensuring
                consistency with your established rates while allowing
                flexibility for market changes.
              </p>
            </div>
            <div className="bg-[#2d2d2d] rounded-lg p-8" data-id="element-151">
              <h3 className="text-xl font-semibold mb-4" data-id="element-152">
                Machine Learning Refinement
              </h3>
              <p className="text-gray-300" data-id="element-153">
                Our platform continuously improves its accuracy by learning from
                your feedback. Each quote you review and adjust helps train the
                model to better understand your specific pricing methodology and
                preferences.
              </p>
            </div>
            <div className="bg-[#2d2d2d] rounded-lg p-8" data-id="element-154">
              <h3 className="text-xl font-semibold mb-4" data-id="element-155">
                Market Intelligence
              </h3>
              <p className="text-gray-300" data-id="element-156">
                Stay competitive with real-time material cost tracking and
                regional pricing insights. Our system monitors market
                fluctuations and alerts you to significant price changes that
                could impact your estimates.
              </p>
            </div>
            <div className="bg-[#2d2d2d] rounded-lg p-8" data-id="element-157">
              <h3 className="text-xl font-semibold mb-4" data-id="element-158">
                Flexible Adjustments
              </h3>
              <p className="text-gray-300" data-id="element-159">
                Easily modify labor rates, material markups, and overhead
                percentages on a per-project basis. Create custom pricing tiers
                for different client types or project complexities while
                maintaining your baseline cost structure.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-160">
            <div className="max-w-2xl mx-auto text-center md:col-span-2" data-id="element-161">
              <h3 className="text-2xl font-semibold mb-4" data-id="element-162">
                Your Data Stays Private
              </h3>
              <p className="text-gray-300" data-id="element-163">
                Your proprietary pricing information is encrypted and never
                shared. Our models learn exclusively from your data to serve
                your business, maintaining complete confidentiality of your
                competitive pricing strategies.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Agentic Workflows */}

      {/* Extract From Drawings */}

      {/* CTA Section */}
      <section className="px-6 md:px-16 py-20" data-id="element-164">
        <div className="max-w-4xl mx-auto text-center" data-id="element-165">
          <h2 className="text-4xl font-bold mb-6" data-id="element-166">
            Ready to Transform Your Estimating Process?
          </h2>
          <p className="text-xl text-gray-300 mb-8" data-id="element-167">
            Join contractors who are winning more bids and saving hours every
            week. Contact us today to schedule a demo of Tape Measure AI
          </p>
          <a href="mailto:james-mares@tapemeasureai.com?subject=Demo Request&body=Hi, I would like to schedule a demo of Tape Measure AI." className="bg-[#d4a855] hover:bg-[#c49945] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-colors inline-block" data-id="element-168">
            Contact for Demo
          </a>
        </div>
      </section>
    </div>;
}